import { lazy, useEffect, useRef } from 'react'
import * as Sentry from '@sentry/tanstackstart-react'
import type { QueryClient } from '@tanstack/react-query'
import {
  HeadContent,
  Link,
  Outlet,
  Scripts,
  createRootRouteWithContext,
  type ErrorComponentProps,
  useRouterState,
} from '@tanstack/react-router'
import { createServerFn } from '@tanstack/react-start'
import { applySecurityHeaders } from '../lib/security-headers'
import { track } from '../lib/track'
import { ClientNav } from '../components/ClientNav'
import appCss from '../styles.css?url'

const ReactQueryDevtools = import.meta.env.DEV
  ? lazy(() =>
      import('@tanstack/react-query-devtools').then((m) => ({
        default: m.ReactQueryDevtools,
      })),
    )
  : () => null

// ---------------------------------------------------------------------------
// Server function: set security headers on every response
// ---------------------------------------------------------------------------

const setHeaders = createServerFn({ method: 'GET' }).handler(async () => {
  applySecurityHeaders()
})

// ---------------------------------------------------------------------------
// Router context
// ---------------------------------------------------------------------------

interface RouterContext {
  queryClient: QueryClient
}

export const Route = createRootRouteWithContext<RouterContext>()({
  beforeLoad: async () => {
    try {
      await setHeaders()
    } catch {
      // Server function may fail during client-side preloads — safe to ignore
      // since security headers only matter on the initial SSR response.
    }
  },
  head: () => ({
    meta: [
      { charSet: 'utf-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1' },
      { title: 'Unbreakable' },
      { name: 'description', content: 'Device Management Dashboard' },
      // OpenGraph
      { property: 'og:type', content: 'website' },
      { property: 'og:title', content: 'Unbreakable - Device Management Dashboard' },
      { property: 'og:description', content: 'Device Management Dashboard' },
      { property: 'og:image', content: 'https://app.unbreakableapp.com/unbreakable.png' },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      // Twitter
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'Unbreakable - Device Management Dashboard' },
      { name: 'twitter:description', content: 'Device Management Dashboard' },
      { name: 'twitter:image', content: 'https://app.unbreakableapp.com/unbreakable.png' },
    ],
    links: [
      { rel: 'stylesheet', href: appCss },
      { rel: 'icon', href: '/unbreakable.png' },
      { rel: 'apple-touch-icon', href: '/icon.png' },
    ],
  }),
  shellComponent: RootDocument,
  component: RootComponent,
  pendingComponent: RootPending,
  errorComponent: RootError,
  notFoundComponent: RootNotFound,
})

function PageViewTracker() {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const previousPathnameRef = useRef<string | null>(null)
  useEffect(() => {
    if (previousPathnameRef.current === pathname) return
    previousPathnameRef.current = pathname
    track({ name: 'page_viewed', props: { page: pathname } })
  }, [pathname])
  return null
}

function RootDocument({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body className="font-sans antialiased">
        {children}
        <Scripts />
      </body>
    </html>
  )
}

function RootPending() {
  return (
    <div className="flex items-center justify-center min-h-[50vh] bg-page-bg" role="status">
      <div className="motion-safe:animate-spin rounded-full h-8 w-8 border-b-2 border-brand" />
      <span className="sr-only">Loading</span>
    </div>
  )
}

function RootError({ error, reset }: ErrorComponentProps) {
  useEffect(() => {
    Sentry.captureException(error)
  }, [error])

  return (
    <div className="flex flex-col items-center justify-center min-h-[50vh] bg-page-bg px-4">
      <h2 className="text-lg font-semibold text-gray-900 mb-2">Something went wrong</h2>
      <p className="text-sm text-gray-600 mb-4 text-center max-w-md">
        {error.message || 'An unexpected error occurred.'}
      </p>
      <button
        onClick={reset}
        className="px-4 py-2.5 text-sm bg-brand text-white rounded-lg hover:bg-brand-hover transition-colors"
      >
        Try again
      </button>
    </div>
  )
}

function RootNotFound() {
  return (
    <div className="flex flex-col items-center justify-center min-h-[50vh] bg-page-bg px-4">
      <h2 className="text-lg font-semibold text-gray-900 mb-2">Page not found</h2>
      <p className="text-sm text-gray-600 mb-4 text-center max-w-md">
        The page you're looking for doesn't exist or has been moved.
      </p>
      <Link
        to="/"
        className="px-4 py-2.5 text-sm bg-brand text-white rounded-lg hover:bg-brand-hover transition-colors"
      >
        Go to Dashboard
      </Link>
    </div>
  )
}

function RootComponent() {
  return (
    <div className="min-h-screen bg-page-bg">
      <PageViewTracker />
      <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[100] focus:bg-white focus:px-4 focus:py-2 focus:rounded-lg focus:shadow-lg focus:text-brand">
        Skip to content
      </a>
      <ClientNav />
      <main id="main-content">
        <Outlet />
      </main>
      <ReactQueryDevtools buttonPosition="bottom-left" />
    </div>
  )
}
